// What's been done on an order so far, at the top of the admin's order page.
//
// The status dropdown only says where an order sits now. This shows every
// step and whether it has actually happened -- so "did I mark this paid?"
// is answered at a glance, even if the order has since moved on. Ticks come
// from the same evidence the customer's tracking page uses (orderProgress).
import React from 'react';
import { Check } from 'lucide-react';
import type { Order, PaymentMethodOption } from '../../types';
import { fullTimeline, stepDate, stepReached } from '../../utils/orderProgress';
import { STATUS_LABEL } from '../../pages/admin/orderUtils';
import { statusLabel } from '../../utils/orderProgress';

// Oct 5 -- the year only when it isn't this one.
const shortDate = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso.length <= 10 ? `${iso}T00:00:00` : iso);
  if (Number.isNaN(d.getTime())) return '';
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
};

interface Props {
  order: Order;
  /** For showing a recorded payment by its label ("Venmo") rather than key. */
  paymentMethods?: PaymentMethodOption[];
}

const OrderProgress: React.FC<Props> = ({ order, paymentMethods = [] }) => {
  const steps = fullTimeline(order);
  const paidWith = order.payment_method
    ? paymentMethods.find(m => m.key === order.payment_method)?.label ?? order.payment_method
    : '';
  const stopped = order.status === 'cancelled' || order.status === 'refunded';

  return (
    <section
      aria-label="Order progress"
      className="bg-white border border-background-dark rounded p-4 space-y-3"
    >
      {stopped && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
          This order is {statusLabel(STATUS_LABEL, order.status, order.fulfillment).toLowerCase()}. The steps below are how far
          it got first.
        </div>
      )}

      <ol className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-0">
        {steps.map((step, i) => {
          const done = stepReached(order, step);
          const current = !stopped && step === order.status;
          const date = done ? shortDate(stepDate(order, step)) : '';
          // The line into this step is solid once the step itself is done.
          const lineDone = done;

          return (
            <li
              key={step}
              data-step={step}
              data-done={done}
              aria-current={current ? 'step' : undefined}
              className="flex sm:flex-col items-center sm:flex-1 gap-3 sm:gap-1.5 min-w-0"
            >
              <div className="flex sm:w-full items-center">
                {/* Connector from the previous step; desktop only. */}
                <div
                  className={`hidden sm:block h-0.5 flex-1 ${
                    i === 0 ? 'invisible' : lineDone ? 'bg-green-600' : 'bg-background-dark'
                  }`}
                />
                <div
                  className={`shrink-0 h-7 w-7 rounded-full flex items-center justify-center border-2 ${
                    done
                      ? stopped
                        ? 'bg-gray-400 border-gray-400 text-white'
                        : 'bg-green-600 border-green-600 text-white'
                      : current
                        ? 'border-primary bg-white'
                        : 'border-background-dark bg-white'
                  } ${current ? 'ring-2 ring-primary ring-offset-2' : ''}`}
                >
                  {done && <Check size={15} strokeWidth={3} />}
                </div>
                <div
                  className={`hidden sm:block h-0.5 flex-1 ${
                    i === steps.length - 1
                      ? 'invisible'
                      : stepReached(order, steps[i + 1])
                        ? 'bg-green-600'
                        : 'bg-background-dark'
                  }`}
                />
              </div>
              <div className="sm:text-center min-w-0">
                <div
                  className={`text-sm leading-tight ${
                    current ? 'font-semibold text-primary' : done ? 'text-text' : 'text-text-light'
                  }`}
                >
                  {statusLabel(STATUS_LABEL, step, order.fulfillment)}
                </div>
                <div className="text-xs text-text-light leading-tight mt-0.5 min-h-[1rem]">
                  {date}
                  {step === 'paid' && done && paidWith && (
                    <span>
                      {date ? ' · ' : ''}
                      {paidWith}
                    </span>
                  )}
                  {current && !done && 'Now'}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
};

export default OrderProgress;
